import React, { useRef } from "react";
import { LogOut, Moon, PanelRightClose, Plus, Settings, Sun, Trash2, Zap } from "lucide-react";
import { ICON_CONFIG } from "../config";

export default function Sidebar({
  user,
  isDark,
  sidebarOpen,
  setSidebarOpen,
  handleNewChat,
  sessions = [],
  activeSessionId,
  pendingSessionIds = new Set(),
  unreadSessionIds = new Set(),
  loadSession,
  deleteSession,
  onThemeToggle,
  setActiveTab,
  setIsSettingsOpen,
  setView,
  onLogout,
}) {
  const displayName = user?.username || (user?.id ? `用户${String(user.id).slice(-4)}` : "账户");
  const displayInitial = displayName.trim()[0] || "L";
  const touchSelectRef = useRef(null);

  const selectSession = (sessionId, forceClose = false) => {
    if (forceClose) setSidebarOpen(false);
    loadSession(sessionId);
  };

  const handleTouchSession = (event, sessionId) => {
    event.preventDefault();
    event.stopPropagation();
    touchSelectRef.current = sessionId;
    selectSession(sessionId, true);
    window.setTimeout(() => {
      if (touchSelectRef.current === sessionId) touchSelectRef.current = null;
    }, 450);
  };

  return (
    <aside className={`app-sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="brand-mark">
            {ICON_CONFIG.logo ? <img src={ICON_CONFIG.logo} alt="LumaNova" /> : <Zap size={18} />}
          </div>
          <span>LumaNova</span>
        </div>
        <button type="button" className="icon-button" onClick={() => setSidebarOpen(false)} aria-label="收起侧边栏">
          <PanelRightClose size={19} />
        </button>
      </div>

      <div className="sidebar-content">
        <button type="button" className="new-chat-button" onClick={handleNewChat}>
          <Plus size={18} />
          <span>新对话</span>
        </button>
        <div className="session-list">
          {sessions.map((session) => {
            const isPending = pendingSessionIds.has(session.session_id);
            const isUnread = unreadSessionIds.has(session.session_id);
            return (
              <div
                key={session.session_id}
                className={`session-row ${activeSessionId === session.session_id ? "active" : ""} ${isPending ? "pending" : ""} ${isUnread ? "unread" : ""}`}
              >
                <button
                  type="button"
                  className="session-title-button"
                  onPointerUp={(event) => {
                    if (event.pointerType && event.pointerType !== "mouse") {
                      handleTouchSession(event, session.session_id);
                    }
                  }}
                  onTouchEnd={(event) => {
                    if (touchSelectRef.current !== session.session_id) {
                      handleTouchSession(event, session.session_id);
                    }
                  }}
                  onClick={(event) => {
                    if (touchSelectRef.current === session.session_id) {
                      event.preventDefault();
                      return;
                    }
                    selectSession(session.session_id);
                  }}
                >
                  <span>{session.title || "新对话"}</span>
                  {isPending && <i className="session-running" aria-label="生成中" />}
                  {!isPending && isUnread && <i className="session-unread" aria-label="有新回复" />}
                </button>
                <button
                  type="button"
                  className="session-delete"
                  onClick={(event) => {
                    event.stopPropagation();
                    deleteSession(session.session_id);
                  }}
                  aria-label="删除会话"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sidebar-footer">
        <button type="button" className="sidebar-action" onClick={onThemeToggle}>
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
          <span>{isDark ? "浅色模式" : "深色模式"}</span>
        </button>
        <button
          type="button"
          className="account-button"
          onClick={() => {
            setActiveTab("keys");
            setIsSettingsOpen(true);
          }}
        >
          <span className="user-initial">{displayInitial}</span>
          <span className="user-name">{displayName}</span>
          <Settings size={18} />
        </button>
        <button type="button" className="sidebar-action danger" onClick={onLogout || (() => setView("login"))}>
          <LogOut size={18} />
          <span>退出登录</span>
        </button>
      </div>
    </aside>
  );
}
